import { Spotlight } from '@atlaskit/onboarding';
import { Box, xcss } from '@atlaskit/primitives';
import Anchor from '@atlaskit/primitives/anchor';
import useCommentContext from '@databases/comments/hooks/useCommentContext';
import { useConditionalCoordination } from '@databases/coordination';
import { useDatabaseContent } from '@databases/database-state';
import { messages } from '@databases/intl/messages';
import { defaultStoreUtils } from '@databases/redux';
import { selectDatabaseDraftByContentId } from '@databases/redux/changesets/selectors';
import { DatabaseLayoutType } from '@databases/types/database/view';
import useHandleOutsideClick from '@databases/utils/hooks/useHandleOutsideClick';
import { getValueId } from '@databases/utils/utils';
import useView from '@databases/views/useView';
import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl-next';

import InlineCommentsOnboarding from './assets/inline-comments-onboarding.gif';

export const IMPORT_ONBOARDING = 'importOnboarding';
export const IMPORT_ONBOARDING_COORDINATION_MESSAGE_KEY =
  'cc-databases-import-onboarding' as const;

export const INLINE_COMMENTS_ONBOARDING = 'inlineCommentsOnboarding';
export const INLINE_COMMENTS_ONBOARDING_COORDINATION_MESSAGE_KEY =
  'cc-databases-inline-comments-onboarding' as const;

export const QUICK_CALC_ONBOARDING = 'quickCalcOnboarding';
export const QUICK_CALC_ONBOARDING_COORDINATION_MESSAGE_KEY =
  'cc-databases-quick-calc-onboarding' as const;

export const VIEWS_CHANGEBOARDING = 'viewsChangeboarding';
export const VIEWS_CHANGEBOARDING_COORDINATION_MESSAGE_KEY =
  'cc-databases-views-changeboarding' as const;

type CoordinationMessageKeys =
  | typeof VIEWS_CHANGEBOARDING_COORDINATION_MESSAGE_KEY
  | typeof QUICK_CALC_ONBOARDING_COORDINATION_MESSAGE_KEY
  | typeof INLINE_COMMENTS_ONBOARDING_COORDINATION_MESSAGE_KEY
  | typeof IMPORT_ONBOARDING_COORDINATION_MESSAGE_KEY;

const ONBOARDING_TOUR_DIALOG_WIDTH = 287;
const VIEWS_CHANGEBOARDING_DOCUMENTATION =
  'https://support.atlassian.com/confluence-cloud/docs/database-views/';

const spotlightLink = xcss({
  color: 'color.text.inverse',
  display: 'inline',
  textDecoration: 'underline',

  ':hover': {
    color: 'color.text.inverse',
    display: 'inline',
    fontWeight: 'bold',
    textDecoration: 'underline',
  },
});

const unreachable = (value: never) => {
  throw new Error(`Not implemented: ${value}`);
};

export const DatabaseSpotlight = ({
  coordinationMessageKey,
  isEnabled,
  targetRef,
  delay = false,
}: {
  coordinationMessageKey: CoordinationMessageKeys;
  isEnabled?: boolean | undefined;
  targetRef?: HTMLElement | null;
  delay?: boolean;
}) => {
  const { formatMessage } = useIntl();
  const { contentId } = useDatabaseContent() as { contentId: string };
  const [firstCellValueId] = defaultStoreUtils.useEqualSelector((state) => {
    if (!contentId) {
      return [null];
    }
    const database = selectDatabaseDraftByContentId(state, {
      contentId: contentId,
      scope: 'main',
      includeDeleted: false,
    });

    return [getValueId(database?.entries[0], database?.fields[0])];
  });
  const [coordinationStarted, stop] = useConditionalCoordination(
    coordinationMessageKey,
    isEnabled ?? true
  );
  const { setOpenThread } = useCommentContext();
  const { view } = useView();
  const [activeSpotlight, setActiveSpotlight] = useState<null | number>(0);

  // Additional constants for spotlight actions
  // const advanceSpotlight = formatMessage(messages['onboarding-tour.advance-spotlight']);
  const dismissSpotlight = formatMessage(messages['onboarding-tour.dismiss-spotlight']);
  const completeSpotlight = formatMessage(messages['databases.ga.spotlight.ok']);
  const completeCommentSpotlight = formatMessage(
    messages['onboarding.inline-comments-complete']
  );
  // Using state to prevent the spotlight from rendering before the delay has finished.
  // Without this state, the spotlight will render immediately.
  const [spotlightDelayHasFinished, setSpotlightDelayHasFinished] = useState(false);

  const start = () => setActiveSpotlight(0);
  // Additional spotlight step
  // const next = () => setActiveSpotlight((activeSpotlight || 0) + 1);
  const end = useCallback(() => {
    stop();
    setActiveSpotlight(null);
  }, [stop]);

  useHandleOutsideClick(end, undefined, [
    '[data-testid="quickCalcOnboarding--dialog"]',
    '[data-testid="inlineCommentsOnboarding--dialog"]',
    '[data-testid="importOnboarding--dialog"]',
  ]);

  useEffect(() => {
    if (coordinationStarted && delay) {
      // When the hyperlinks resolve, it causes a content shift in the database.
      // This causes the spotlight to be in the wrong position. We fix this problem
      // by waiting to show the spotlight.
      setTimeout(() => {
        setSpotlightDelayHasFinished(true);
        start();
      }, 5000);
    }
  }, [coordinationStarted, delay]);

  const viewsChangeboardingSpotlights = [
    <Spotlight
      actions={[
        {
          onClick: () => end(),
          text: completeSpotlight,
        },
      ]}
      heading={formatMessage(messages['changeboarding.views-heading'])}
      key={VIEWS_CHANGEBOARDING}
      target={VIEWS_CHANGEBOARDING}
      testId={VIEWS_CHANGEBOARDING}
      dialogWidth={ONBOARDING_TOUR_DIALOG_WIDTH}
      pulse
    >
      <Box>
        <FormattedMessage
          {...messages['changeboarding.views-message']}
          values={{
            // @ts-expect-error
            a: (chunks: React.ReactNode) => (
              <Anchor
                href={VIEWS_CHANGEBOARDING_DOCUMENTATION}
                target="_blank"
                rel="noreferrer"
                xcss={spotlightLink}
              >
                {chunks}
              </Anchor>
            ),
          }}
        />
      </Box>
    </Spotlight>,
  ];

  const quickCalcOnboardingSpotlights = [
    <Spotlight
      actions={[
        {
          onClick: () => end(),
          text: completeSpotlight,
        },
      ]}
      heading={formatMessage(messages['onboarding.quick-calc-heading'])}
      key={QUICK_CALC_ONBOARDING}
      target={QUICK_CALC_ONBOARDING}
      targetNode={targetRef ? targetRef : undefined}
      testId={QUICK_CALC_ONBOARDING}
      dialogWidth={ONBOARDING_TOUR_DIALOG_WIDTH}
      dialogPlacement="right bottom"
      pulse
    >
      <Box>
        <FormattedMessage {...messages['onboarding.quick-calc-message']} />
      </Box>
    </Spotlight>,
  ];

  const toggleComment = useCallback(() => {
    if (!firstCellValueId) {
      stop();
      setActiveSpotlight(null);
      return;
    }

    if (view.settings.layout.type === DatabaseLayoutType.TABLE) {
      setOpenThread({
        valueId: firstCellValueId,
        panel: false,
      });
    } else {
      setOpenThread({
        valueId: firstCellValueId,
        panel: true,
      });
    }

    stop();
    setActiveSpotlight(null);
  }, [firstCellValueId, setOpenThread, stop, view.settings.layout.type]);

  const inlineCommentsOnboardingSpotlights = [
    <Spotlight
      actions={[
        {
          onClick: () => toggleComment(),
          text: completeCommentSpotlight,
        },
        { onClick: () => end(), text: dismissSpotlight },
      ]}
      heading={formatMessage(messages['onboarding.inline-comments-heading'])}
      key={INLINE_COMMENTS_ONBOARDING}
      target={INLINE_COMMENTS_ONBOARDING}
      image={InlineCommentsOnboarding}
      targetNode={targetRef ? targetRef : undefined}
      testId={INLINE_COMMENTS_ONBOARDING}
      dialogWidth={ONBOARDING_TOUR_DIALOG_WIDTH}
      dialogPlacement="right bottom"
      pulse={false}
    >
      <Box>
        <FormattedMessage {...messages['onboarding.inline-comments-message']} />
      </Box>
    </Spotlight>,
  ];

  const importOnboardingSpotlights = [
    <Spotlight
      actions={[
        {
          onClick: () => end(),
          text: completeSpotlight,
        },
      ]}
      heading={formatMessage(messages['onboarding.import-heading'])}
      key={IMPORT_ONBOARDING}
      target={IMPORT_ONBOARDING}
      targetNode={targetRef ? targetRef : undefined}
      testId={IMPORT_ONBOARDING}
      dialogWidth={ONBOARDING_TOUR_DIALOG_WIDTH}
      dialogPlacement="left bottom"
      pulse
    >
      <Box>
        <FormattedMessage {...messages['onboarding.import-message']} />
      </Box>
    </Spotlight>,
  ];

  const getSpotlights = (coordinationMessageKey: CoordinationMessageKeys) => {
    switch (coordinationMessageKey) {
      case VIEWS_CHANGEBOARDING_COORDINATION_MESSAGE_KEY:
        return viewsChangeboardingSpotlights;
      case QUICK_CALC_ONBOARDING_COORDINATION_MESSAGE_KEY:
        return quickCalcOnboardingSpotlights;
      case INLINE_COMMENTS_ONBOARDING_COORDINATION_MESSAGE_KEY:
        return inlineCommentsOnboardingSpotlights;
      case IMPORT_ONBOARDING_COORDINATION_MESSAGE_KEY:
        return importOnboardingSpotlights;
      default:
        return unreachable(coordinationMessageKey);
    }
  };

  const renderActiveSpotlight = () => {
    if (activeSpotlight === null) {
      return null;
    }

    const spotlights = getSpotlights(coordinationMessageKey);
    return spotlights ? spotlights[activeSpotlight] : null;
  };

  return (
    <>
      {coordinationStarted &&
        (!delay || spotlightDelayHasFinished) &&
        renderActiveSpotlight()}
    </>
  );
};
